import React from "react"

import {TaskCardItem} from "../../components/TaskCard/types"
import {FeatureItem} from "../../components/FeatureItemCard/types"
import {ProgramPerformanceItem} from "../../components/ProgramPerformanceItemCard/types"
import classNames from "classnames"
import {GettingStartedCard} from "../../components/ManagedSolutionsHomepage/GettingStartedCard/GettingStartedCard"
import {ProgramPerformanceCard} from "../../components/ManagedSolutionsHomepage/ProgramPerformanceCard/ProgramPerformanceCard"
import {FeaturesCard} from "../../components/ManagedSolutionsHomepage/FeaturesCard/FeaturesCard"
import {LegalTermsCard} from "../../components/ManagedSolutionsHomepage/LegalTermsCard/LegalTermsCard"
import {useAccessToken} from "../../services/auth"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {AsyncResult} from "../../types/asyncResult"
import Loading from "../../components/Loading/Loading"
import {getManagedSolutionsStatus, GettingStartedAttributes} from "../../resources/managedSolutionsStatus"
import {featuresItems, getMainDisclaimer} from "../../components/ManagedSolutionsHomepage/FeaturesCard/data"
import {useUserClaimsData} from "../../services/auth"
import {
	getPerformanceItems,
	PerformanceKey,
} from "../../components/ManagedSolutionsHomepage/ProgramPerformanceCard/getPerformanceItems"
import {Account, findAccountsHandler, getAccountsMetrics} from "../../resources/account"
import {OkDocument} from "../../resources/common"
import {findManagedSolutionsCustomers, findManagedSolutionsAccounts} from "../../resources/managedSolutions"
import {useModal} from "react-modal-hook"
import {CopyCodeModal} from "../../components/CopyCodeModal/CopyCodeModal"
import {useNavigate} from "react-router-dom"
import {NavigateFunction} from "react-router/dist/lib/hooks"
import {useIsOrgManagedSolutionsOperationalAdminOnly, useIsManagedSolutionsBankingUser} from "../../services/auth"
import {OrgAccountCard} from "../../components/ManagedSolutionsHomepage/OrgAccountCard/OrgAccountCard"

const getGettingStartedTasks = ({
	attributes,
	showCopyCodeModal,
	navigate,
}: {
	attributes?: GettingStartedAttributes
	showCopyCodeModal: () => void
	navigate: NavigateFunction
}): TaskCardItem[] => {
	const {setupAuthentication, embedTheComponent, setupPrefill} = attributes ?? {}
	return [
		{
			title: "Review the docs",
			description: "Read our simple guide to understand the implementation process",
			duration: "10 minutes",
			icon: "programming-book",
			isDone: setupAuthentication || embedTheComponent || setupPrefill,
			url: "https://unit.co/docs/ready-to-launch",
		},
		{
			title: "Set up end-user authentication",
			description: "Configure your Identity provider to allow for seamless authentication",
			duration: "15 minutes",
			icon: "lock-square-lock-1--interface-essential",
			isDone: setupAuthentication,
			onClick: () => {
				navigate("/managed-solutions/settings")
			},
		},
		{
			title: "Embed the Managed Solutions component into your app",
			description: "Read the getting started guide",
			duration: "30 minutes",
			icon: "programming-code-terminal-2",
			isDone: embedTheComponent,
			onClick: showCopyCodeModal,
		},
		{
			title: "Set up your callback endpoints (optional)",
			description: "Streamline the end user experience by pre-filling information you already have",
			duration: "1-2 days",
			icon: "checklist-pen--interface-essential",
			isDone: setupPrefill,
			onClick: () => {
				navigate("/managed-solutions/settings#tab=Callbacks")
			},
		},
	]
}

interface ManagedSolutionsHomepageProps {
	className?: string
	tasks: TaskCardItem[]
	performanceItems: ProgramPerformanceItem[]
	features: FeatureItem[]
	defaultExpandedGettingStarted?: boolean
	mainDisclaimer?: {id: string; description: string | string[]}
}

interface ManagedSolutionsHomepageViewProps extends ManagedSolutionsHomepageProps {
	orgAccountCard?: React.ReactNode
	isManagedSolutionsBankingUser?: boolean
}

export function ManagedSolutionsHomepageView({
	tasks,
	performanceItems,
	features,
	className,
	defaultExpandedGettingStarted = true,
	mainDisclaimer,
	orgAccountCard,
	isManagedSolutionsBankingUser = false,
}: ManagedSolutionsHomepageViewProps) {
	return (
		<div className={classNames("managed-solutions-homepage", className)}>
			<div className="managed-solutions-homepage-content">
				<h2 className="homepage-title">Welcome to Managed Solutions</h2>
				<GettingStartedCard
					className="getting-started-card"
					tasks={tasks}
					defaultExpanded={defaultExpandedGettingStarted}
				/>
				{orgAccountCard}
				<ProgramPerformanceCard className="program-performance-card" items={performanceItems} />
				{isManagedSolutionsBankingUser ? (
					<LegalTermsCard mainDisclaimer={mainDisclaimer} />
				) : (
					<FeaturesCard features={features} mainDisclaimer={mainDisclaimer} />
				)}
			</div>
		</div>
	)
}

function ManagedSolutionsHomepageAdmin() {
	const accessToken = useAccessToken()
	const claims = useUserClaimsData()
	const isOperationalAdminOnly = useIsOrgManagedSolutionsOperationalAdminOnly()
	const status = useAsyncResult(() => getManagedSolutionsStatus(accessToken))
	const customers = useAsyncResult(() => findManagedSolutionsCustomers(accessToken, 0, 1))
	const accounts = useAsyncResult(() => findManagedSolutionsAccounts(accessToken, 0, 10000))
	const operationalAccounts = useAsyncResult(() =>
		findManagedSolutionsAccounts(accessToken, 0, 100, undefined, {purpose: "operational_account"})
	)
	const [showCopyCodeModal, hideCopyCodeModal] = useModal(() => <CopyCodeModal close={hideCopyCodeModal} />)
	const navigate = useNavigate()

	const statusResult = status.match(
		() => null,
		(value) => value,
		() => null
	)
	const customersResult = customers.match(
		() => null,
		(value) => value,
		() => null
	)
	const accountsResult = accounts.match(
		() => null,
		(value) => value,
		() => null
	)
	const operationalAccountsResult = operationalAccounts.match(
		() => null,
		(value) => value,
		() => null
	)

	if (!statusResult || !customersResult) {
		return <Loading />
	}

	const gettingStartedTasks = getGettingStartedTasks({
		attributes: statusResult.data.attributes,
		showCopyCodeModal,
		navigate,
	})

	const accountsTotal = accountsResult?.meta?.pagination?.total ?? 0
	const depositBalance = !isOperationalAdminOnly
		? (accountsResult?.data || []).reduce((sum, account) => sum + (account.attributes.balance ?? 0), 0)
		: 0

	const performanceData: Record<PerformanceKey, string> = {
		customers: customersResult.meta?.pagination.total.toString() ?? "N/A",
		accounts: accountsTotal.toString(),
		depositBalance: depositBalance.toString(),
	}
	const orgName = claims.org || "Unit"
	const mainDisclaimer = getMainDisclaimer(orgName, "i3 Bank")
	const operationalAccountsList = operationalAccountsResult?.data || []
	const orgAccountCard =
		operationalAccountsList.length > 0 ? <OrgAccountCard accounts={operationalAccountsList} /> : undefined

	return (
		<ManagedSolutionsHomepageView
			tasks={gettingStartedTasks}
			performanceItems={getPerformanceItems(performanceData, navigate, {
				excludeKeys: isOperationalAdminOnly ? ["depositBalance"] : undefined,
			})}
			features={featuresItems}
			defaultExpandedGettingStarted={false}
			mainDisclaimer={mainDisclaimer}
			orgAccountCard={orgAccountCard}
			isManagedSolutionsBankingUser={true}
		/>
	)
}

export function ManagedSolutionsHomepage() {
	const isManagedSolutionsBankingUser = useIsManagedSolutionsBankingUser()

	if (isManagedSolutionsBankingUser) {
		return <ManagedSolutionsHomepageAdmin />
	}
	const accessToken = useAccessToken()
	const claims = useUserClaimsData()
	const status = useAsyncResult(() => getManagedSolutionsStatus(accessToken))
	const customers = useAsyncResult(() => findManagedSolutionsCustomers(accessToken, 0, 1))
	const accounts = useAsyncResult(() =>
		findAccountsHandler({
			accessToken,
			offset: 0,
			types: [],
			requestType: "json",
		})
	)
	const accountMetrics = useAsyncResult(() => getAccountsMetrics(accessToken))
	const operationalAccounts = useAsyncResult(() =>
		findManagedSolutionsAccounts(accessToken, 0, 100, undefined, {purpose: "operational_account"})
	)
	const [showCopyCodeModal, hideCopyCodeModal] = useModal(() => <CopyCodeModal close={hideCopyCodeModal} />)
	const navigate = useNavigate()

	return AsyncResult.zip4(status, customers, accounts, accountMetrics).match(
		() => <Loading />,
		([status, customers, accounts, accountMetrics]) => {
			const gettingStartedTasks = getGettingStartedTasks({
				attributes: status.data.attributes,
				showCopyCodeModal,
				navigate,
			})
			const performanceData: Record<PerformanceKey, string> = {
				customers: customers.meta?.pagination.total.toString() ?? "N/A",
				accounts: (accounts as OkDocument<Account[]>).meta?.pagination.total.toString() ?? "N/A",
				depositBalance: accountMetrics.data.attributes.totalDepositBalance.toString(),
			}
			const orgName = claims.org || "Unit"
			const mainDisclaimer = getMainDisclaimer(orgName, "i3 Bank")
			const operationalAccountsList = operationalAccounts.match(
				() => [],
				(result) => result.data,
				() => []
			)
			const orgAccountCard =
				operationalAccountsList.length > 0 ? <OrgAccountCard accounts={operationalAccountsList} /> : undefined

			return (
				<ManagedSolutionsHomepageView
					tasks={gettingStartedTasks}
					performanceItems={getPerformanceItems(performanceData, navigate)}
					features={featuresItems}
					mainDisclaimer={mainDisclaimer}
					orgAccountCard={orgAccountCard}
				/>
			)
		},
		(_) => null
	)
}
